import { AccessorFn, ColumnDef, DeepKeys } from "@tanstack/react-table";
import { TableCell } from "@/components/ui/table";
import { Duration } from "luxon";
import { CellHoverClassName } from "@/components/List/ListTable/TableBody/Rows/TableDataRow";

type DurationColumnOptions<T> = ColumnDef<T> & {
	accessorKey: DeepKeys<T> | AccessorFn<T>;
	header?: string;
	align?: "left" | "center" | "right";
	className?: string;
	durationClassName?: string;
	units?: ("years" | "months" | "days" | "hours" | "minutes" | "seconds")[];
	fallback?: string;
};

export const DurationColumn = <T,>({
	accessorKey,
	header,
	align = "right",
	className = "",
	durationClassName = "",
	units = ["days", "hours", "minutes"],
	fallback = "—",
	meta,
	...rest
}: DurationColumnOptions<T>): ColumnDef<T> => {
	const exportFormat = (value: unknown) => {
		const seconds = Number(value);
		if (isNaN(seconds) || seconds <= 0) return "";

		const base = Duration.fromObject({ seconds }).shiftTo(...units);
		const filtered = base.shiftTo(...units.filter(unit => base.get(unit) > 0));

		return filtered.toHuman({ maximumFractionDigits: 0 });
	};

	return {
		accessorKey: accessorKey as string,
		header,
		meta: { wrapWithTableCell: false, exportFormat, cellClassName: className, ...meta },
		cell: ({ getValue }) => {
			const raw = getValue();
			const seconds = Number(raw);

			const alignment = {
				left: "text-left",
				center: "text-center",
				right: "text-right",
			}[align];

			if (isNaN(seconds) || seconds <= 0)
				return (
					<TableCell className={`${CellHoverClassName} ${alignment} ${className}`}>
						<div className={`p-2 ${durationClassName}`}>{fallback}</div>
					</TableCell>
				);

			const base = Duration.fromObject({ seconds }).shiftTo(...units);
			const filtered = base.shiftTo(...units.filter(unit => base.get(unit) > 0));

			const formatted = filtered.toHuman({
				maximumFractionDigits: 0,
				listStyle: "narrow",
			});

			return (
				<TableCell className={`${CellHoverClassName} ${alignment} ${className}`}>
					<div className={`p-2 ${durationClassName}`}>{formatted}</div>
				</TableCell>
			);
		},
		...rest,
	};
};
